import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { BOMB, CLOUDS, DISPLAY, HARP, HITS, LAYERS, SEQUENCES } from './bgData';
import { paint } from './display';
import type { Voice } from './harpAudio';
import { GRENADE, useGrenade } from './useGrenade';
import { useHarp } from './useHarp';
import { useKulcina } from './useKulcina';
import { KulcinaEditor } from './KulcinaEditor';
import { Starfield } from './Starfield';

/**
 * Avalski toranj's beacon colour is the harp's voice. Keyed by colour rather
 * than by step, so reordering the sequence in the prototype keeps every colour
 * sounding the way it looks.
 */
const BEACON_VOICE: Record<string, Voice> = {
  red: 'harp',
  yellow: 'guitar',
  green: 'chip',
  blue: 'musicbox',
};

/**
 * The night sky, the Belgrade skyline, and the landmarks you can poke.
 *
 * Anchored to the DOCUMENT bottom rather than the viewport, so the city sits at
 * the foot of the page however tall it gets — which is why `.night` is
 * positioned: against a static ancestor `bottom:0` would resolve to the
 * viewport-sized initial containing block and land a screen-height from the top.
 */
export function NightSky() {
  // Step 0 is the state the markup loads in, so the first click lands on step 1.
  const [steps, setSteps] = useState<Record<string, number>>({});
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState(false);

  // The display is shared state now: one row in Postgres, live over the socket.
  // Everything about that lives in the hook; this component just draws it.
  const kulcina = useKulcina();
  const { bits, watch } = kulcina;
  // The Ada bridge's 22 stay cables are a harp; the hook owns the sound, the
  // hit geometry and the flash, and hands back the two things to mount.
  const beacon = SEQUENCES.avalski?.[steps.avalski ?? 0] ?? [];
  const voice = beacon.map((light) => BEACON_VOICE[light]).find((v) => v !== undefined);
  const harp = useHarp(voice ?? 'harp');
  // The dome of Hram Svetog Save comes off in your hand. The band is what the
  // blast shakes, so the hook gets a ref to it — and the church's own windows,
  // because a Holy Hand Grenade will not go off over a darkened church.
  const bandRef = useRef<HTMLDivElement>(null);
  const grenade = useGrenade(bandRef, !toggled.sava);
  const skyCanvas = useRef<HTMLCanvasElement>(null);

  // Repaints come from the hook, not from React state — a drag fires ~60
  // pointer moves a second and re-rendering the whole skyline for each one is
  // work with nothing to show for it.
  useEffect(() => {
    const draw = () => {
      const ctx = skyCanvas.current?.getContext('2d');
      if (ctx) paint(ctx, bits, false);
    };
    draw();
    return watch(draw);
  }, [bits, watch]);

  const click = useCallback((building: string) => {
    // Kulčina owns a drawable display, so its click opens that rather than
    // dousing anything — its window lights stay lit for good.
    if (building === 'kulcina') {
      setEditing(true);
      return;
    }
    if (SEQUENCES[building]) {
      setSteps((s) => ({ ...s, [building]: ((s[building] ?? 0) + 1) % SEQUENCES[building]!.length }));
      return;
    }
    setToggled((t) => ({ ...t, [building]: !t[building] }));
  }, []);

  /** A lights layer is dark unless its step names it, or its building is untoggled. */
  const isOff = (building: string | undefined, light: string | undefined): boolean => {
    if (!building) return false;
    const seq = SEQUENCES[building];
    if (!seq) return Boolean(toggled[building]);
    return !seq[steps[building] ?? 0]!.includes(light ?? '');
  };

  return (
    <>
      <div className="bg-sky" aria-hidden="true" />
      <div className="bg" ref={bandRef} aria-hidden="true">
        <Starfield />

        {CLOUDS.map((c, i) => (
          <img
            key={i}
            className="bg-cloud"
            src={c.src}
            alt=""
            draggable={false}
            style={
              {
                left: c.x,
                top: c.y,
                width: c.w,
                height: c.h,
                ['--dx' as string]: `${c.dx}px`,
                ['--dur' as string]: `${c.dur}s`,
                ['--delay' as string]: `${c.delay}s`,
              } as CSSProperties
            }
          />
        ))}

        {LAYERS.map((l, i) => (
          <Fragment key={i}>
            {l.src === BOMB.src ? (
              /* Not a layer so much as a prop: the church's dome, which is a
                 Holy Hand Grenade you can pull off it and throw. It stays in
                 the stack at its docked place — behind the church — and the
                 hook moves it from here by writing its transform. */
              <img
                className="bg-layer bomb"
                ref={grenade.spriteRef}
                src={l.src}
                alt=""
                draggable={false}
                style={{
                  left: l.x,
                  top: l.y,
                  width: l.w,
                  height: l.h,
                  // It tumbles about the ball, not about the corner of its box.
                  transformOrigin: `${BOMB.cx}px ${BOMB.cy}px`,
                }}
              />
            ) : (
              <img
                className={`bg-layer${l.lights ? ' is-lights' : ''}${
                  l.lights && isOff(l.building, l.light) ? ' off' : ''
                }`}
                src={l.src}
                alt=""
                draggable={false}
                style={{ left: l.x, top: l.y, width: l.w, height: l.h }}
              />
            )}
            {/* A plucked cable lights up in its own pixels. The canvas is
                mounted right behind the next layer rather than on top of the
                stack, so Avalski toranj and Beograđanka hide the lit cable
                exactly as they hide the dark one — no clipping needed. */}
            {l.src === HARP.after && (
              <canvas
                className="bg-layer"
                ref={harp.canvasRef}
                width={HARP.w}
                height={HARP.h}
                aria-hidden="true"
                style={{ left: HARP.x, top: HARP.y, width: HARP.w, height: HARP.h }}
              />
            )}
          </Fragment>
        ))}

        {/* Kulčina's display is a canvas, not a sprite: it is whatever someone
            last drew on it. */}
        <canvas
          className="bg-layer"
          ref={skyCanvas}
          width={DISPLAY.w}
          height={DISPLAY.h}
          aria-hidden="true"
          style={{ left: DISPLAY.x, top: DISPLAY.y, width: DISPLAY.w, height: DISPLAY.h }}
        />

        {/* The explosion, parked on wherever it went off. Over everything,
            because by then the grenade is over everything too. */}
        <canvas
          className="bomb-blast"
          ref={grenade.blastRef}
          width={GRENADE.BLAST_R * 2}
          height={GRENADE.BLAST_R * 2}
          aria-hidden="true"
        />

        {/* Hit regions are an exact rectangle cover of each building's alpha,
            not a traced path — pixel art is axis-aligned, so a run-length
            decomposition is exact and needs no tracing library. */}
        <svg className="bg-hit" viewBox="0 0 1920 3690" preserveAspectRatio="none">
          {/* The harp's press-catcher is a plain box over the bridge, so a
              sweep can start in the sky beside the fan. It goes FIRST, which is
              what keeps the bridge behind the city: every building's group is
              painted after it and therefore wins the click, so pressing
              Avalski toranj or Beograđanka toggles the building and never
              plucks the cables running behind it. */}
          <g className="bg-harp" ref={harp.groupRef} {...harp.handlers}>
            <rect
              x={HARP.x - 14}
              y={HARP.y - 14}
              width={HARP.w + 28}
              height={HARP.h + 28}
            />
          </g>
          {HITS.map((h) => (
            <g key={h.building} data-building={h.building} onClick={() => click(h.building)}>
              <title>{h.title}</title>
              {h.rects.map((r, i) => (
                <rect key={i} x={r[0]} y={r[1]} width={r[2]} height={r[3]} />
              ))}
            </g>
          ))}
          {/* LAST, and a circle that follows the grenade: mid-flight it is in
              front of whatever it is passing, so the press is its own. Hram
              Svetog Save's own rects stop at the church, so the hole the dome
              leaves behind is sky rather than a click target. */}
          <g className="bg-bomb" {...grenade.handlers}>
            <title>{BOMB.title}</title>
            <circle ref={grenade.hitRef} cx={BOMB.x + BOMB.cx} cy={BOMB.y + BOMB.cy} r={BOMB.r} />
          </g>
        </svg>
      </div>

      <KulcinaEditor open={editing} kulcina={kulcina} onClose={() => setEditing(false)} />
    </>
  );
}
